// Rich-text editor for HTML message templates (Settings → תבניות הודעות, e.g. the signing-page
// terms). Self-contained, no external lib: a contenteditable surface with a small toolbar that
// drives document.execCommand. Usage: `<textarea data-richtext>` holding the HTML; the textarea
// stays the form field (hidden) and is kept in sync with the editor, so the form posts as before.
//
// Everything that goes in or out passes through `cleanHtml`, an allow-list that keeps the
// structure (headings, paragraphs, lists, bold/italic/underline, links, tables) and drops the
// rest — notably what Google Docs and Word paste in (per-span fonts, sizes, colours, guid ids).
// The body is rendered raw on the public signing page, so the allow-list is also its guard.
// Token chips next to the editor (`[data-rt-token]`) insert their token at the caret.

const ICON = (paths: string, flip = false): string =>
  `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"${flip ? ' class="rt-flip"' : ""} aria-hidden="true">${paths}</svg>`;

const ICONS = {
  bold: ICON('<path d="M6 12h9a4 4 0 0 1 0 8H7a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1h7a4 4 0 0 1 0 8"/>'),
  italic: ICON('<line x1="19" x2="10" y1="4" y2="4"/><line x1="14" x2="5" y1="20" y2="20"/><line x1="15" x2="9" y1="4" y2="20"/>'),
  underline: ICON('<path d="M6 4v6a6 6 0 0 0 12 0V4"/><line x1="4" x2="20" y1="20" y2="20"/>'),
  ul: ICON('<path d="M3 12h.01"/><path d="M3 18h.01"/><path d="M3 6h.01"/><path d="M8 12h13"/><path d="M8 18h13"/><path d="M8 6h13"/>', true),
  ol: ICON('<path d="M10 12h11"/><path d="M10 18h11"/><path d="M10 6h11"/><path d="M4 10h2"/><path d="M4 6h1v4"/><path d="M6 18H4c0-1 2-2 2-3s-1-1.5-2-1"/>', true),
  alignRight: ICON('<path d="M21 12H9"/><path d="M21 18H7"/><path d="M21 6H3"/>'),
  alignCenter: ICON('<path d="M17 12H7"/><path d="M19 18H5"/><path d="M21 6H3"/>'),
  alignLeft: ICON('<path d="M15 12H3"/><path d="M17 18H3"/><path d="M21 6H3"/>'),
  link: ICON('<path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/>'),
  clear: ICON('<path d="M4 7V4h16v3"/><path d="M5 20h6"/><path d="M13 4 8 20"/><path d="m15 15 5 5"/><path d="m20 15-5 5"/>'),
  undo: ICON('<path d="M9 14 4 9l5-5"/><path d="M4 9h10.5a5.5 5.5 0 0 1 5.5 5.5a5.5 5.5 0 0 1-5.5 5.5H11"/>', true),
  redo: ICON('<path d="m15 14 5-5-5-5"/><path d="M20 9H9.5A5.5 5.5 0 0 0 4 14.5A5.5 5.5 0 0 0 9.5 20H13"/>', true),
  code: ICON('<path d="m16 18 6-6-6-6"/><path d="m8 6-6 6 6 6"/>'),
};

// ---- Sanitizer ------------------------------------------------------------------------------

const BLOCK = new Set(["P", "H4", "H5", "UL", "OL", "LI", "TABLE", "THEAD", "TBODY", "TR", "TH", "TD", "HR"]);
const INLINE = new Set(["STRONG", "EM", "U", "A", "BR"]);
// Removed with their content; anything else not allowed is unwrapped (its text is kept).
const DROP = new Set([
  "SCRIPT", "STYLE", "META", "LINK", "TITLE", "HEAD", "IFRAME", "FRAME", "OBJECT", "EMBED", "SVG",
  "MATH", "IMG", "PICTURE", "VIDEO", "AUDIO", "CANVAS", "FORM", "INPUT", "BUTTON", "SELECT",
  "TEXTAREA", "NOSCRIPT", "TEMPLATE", "COLGROUP", "COL",
]);
const RENAME: Record<string, string> = { B: "STRONG", I: "EM", H1: "H4", H2: "H4", H3: "H4", H6: "H5" };
const ALIGNABLE = new Set(["P", "H4", "H5", "LI", "TH", "TD"]);
const SAFE_HREF = /^(https?:|mailto:|tel:)/i;

/** Allow-listed copy of `html`: see the header comment. Parsed in an inert document, so nothing
 *  in the input runs. Returns "" when no text is left (the server then falls back to the default).
 *  Blocks are put one per line for whoever reads the source; `sep` "" is for pasting, where
 *  Chrome would turn each of those line breaks into an empty paragraph. */
export function cleanHtml(html: string, sep = "\n"): string {
  const doc = new DOMParser().parseFromString(`<body>${html}</body>`, "text/html");
  const root = doc.body;
  cleanChildren(root, doc);
  wrapLooseInline(root, doc);
  if (!root.textContent?.trim()) return "";
  // Empty paragraphs at either end (the caret's placeholder line) are not content.
  const empty = (el: Element | null) => el?.tagName === "P" && !el.textContent?.trim();
  while (empty(root.firstElementChild)) root.firstElementChild!.remove();
  while (empty(root.lastElementChild)) root.lastElementChild!.remove();
  return Array.from(root.children).map((el) => el.outerHTML).join(sep);
}

const NESTABLE = "p, h4, h5";
const BLOCKS_INSIDE = ":scope > p, :scope > h4, :scope > h5, :scope > ul, :scope > ol, :scope > table, :scope > hr";

/** Browsers paste paragraphs into a heading, or turn a line into a list, by nesting the new blocks
 *  inside the old one. Lift them out in place, so the editor shows what will be saved; text that
 *  sat beside them keeps the outer block's tag. Moving nodes resets the selection, so the caret
 *  is put back where it was. */
function flatten(editor: HTMLElement): void {
  const outers = Array.from(editor.querySelectorAll<HTMLElement>(NESTABLE))
    .reverse() // innermost first
    .filter((el) => el.querySelector(BLOCKS_INSIDE));
  if (!outers.length) return;
  const sel = document.getSelection();
  const caret = sel?.focusNode ? { node: sel.focusNode, offset: sel.focusOffset } : null;
  for (const outer of outers) {
    let run: HTMLElement | null = null;
    for (const child of Array.from(outer.childNodes)) {
      if (child.nodeType === Node.ELEMENT_NODE && (child as Element).matches("p, h4, h5, ul, ol, table, hr")) {
        outer.before(child);
        run = null;
      } else if (child.textContent?.trim()) {
        if (!run) {
          run = outer.cloneNode(false) as HTMLElement;
          outer.before(run);
        }
        run.appendChild(child);
      } else if (run && child.nodeName === "BR") {
        run.appendChild(child);
      }
    }
    outer.remove();
  }
  if (caret && editor.contains(caret.node)) sel!.collapse(caret.node, caret.offset);
}

function cleanChildren(parent: Element, doc: Document): void {
  for (const child of Array.from(parent.childNodes)) {
    if (child.nodeType === Node.TEXT_NODE) continue;
    if (child.nodeType !== Node.ELEMENT_NODE) {
      child.remove(); // comments, processing instructions
      continue;
    }
    const el = child as HTMLElement;
    const tag = el.tagName.toUpperCase();
    if (DROP.has(tag)) {
      el.remove();
      continue;
    }
    cleanChildren(el, doc); // children first, so an unwrapped element hands over clean content
    el.replaceWith(cleanElement(el, tag, doc));
  }
}

function cleanElement(el: HTMLElement, tag: string, doc: Document): Node {
  const st = el.style;
  const weight = st.fontWeight;
  const bold = weight === "bold" || weight === "bolder" || +weight >= 600;
  const italic = st.fontStyle === "italic";
  // Docs underlines link text with a span; inside a link that is just how links look.
  const underline = /underline/.test(st.textDecoration || st.textDecorationLine || "") && !el.parentElement?.closest("a");
  const hasBlockChild = Array.from(el.children).some((c) => BLOCK.has(c.tagName));
  const blank = !el.textContent?.trim();

  let target = RENAME[tag] ?? tag;
  if (tag === "DIV" || tag === "SECTION" || tag === "ARTICLE" || tag === "BLOCKQUOTE") target = "P";
  // Google Docs wraps the whole paste in <b style="font-weight:normal">; Word marks text the same way.
  if (tag === "B" && weight && !bold) target = "";
  // A paragraph holding blocks is only a wrapper; a paragraph inside a list item is Docs' markup.
  if ((target === "P" || target === "H4" || target === "H5") && hasBlockChild) target = "";
  if (target === "P" && el.parentElement?.tagName === "LI") target = "";
  if (!BLOCK.has(target) && !INLINE.has(target)) target = "";
  // Formatting around nothing but whitespace (an empty <b> the editor left behind) is dropped,
  // keeping the whitespace itself so the words on either side stay apart.
  if (blank && ["STRONG", "EM", "U", "A"].includes(target)) target = "";

  let href = "";
  if (target === "A") {
    href = (el.getAttribute("href") ?? "").trim();
    if (!SAFE_HREF.test(href)) target = "";
  }

  const out = target ? doc.createElement(target) : doc.createDocumentFragment();
  if (target === "A") {
    (out as Element).setAttribute("href", href);
    (out as Element).setAttribute("target", "_blank");
    (out as Element).setAttribute("rel", "noopener noreferrer");
  }
  if (ALIGNABLE.has(target)) {
    const align = (el.getAttribute("align") || st.textAlign || "").toLowerCase();
    if (["left", "right", "center", "justify"].includes(align)) (out as Element).setAttribute("style", `text-align: ${align}`);
    if (el.getAttribute("dir")?.toLowerCase() === "ltr") (out as Element).setAttribute("dir", "ltr");
  }

  // Formatting that lived in a style attribute (Docs: <span style="font-weight:700">) becomes a tag.
  let inner: ParentNode = out;
  const wrap = (t: string) => {
    const w = doc.createElement(t);
    inner.appendChild(w);
    inner = w;
  };
  if (!blank) {
    if (bold && !["STRONG", "H4", "H5", "TH"].includes(target)) wrap("STRONG");
    if (italic && target !== "EM") wrap("EM");
    if (underline && target !== "U" && target !== "A") wrap("U");
  }
  while (el.firstChild) inner.appendChild(el.firstChild);
  return out;
}

/** Text and inline tags directly at the top level are grouped into paragraphs, and runs that hold
 *  nothing but line breaks (Docs puts a <br> between every two paragraphs) are dropped. */
function wrapLooseInline(root: HTMLElement, doc: Document): void {
  let run: Node[] = [];
  const flush = (before: Node | null) => {
    const hasText = run.some((n) => n.textContent?.trim());
    if (hasText) {
      const p = doc.createElement("p");
      root.insertBefore(p, before);
      for (const n of run) p.appendChild(n);
    } else {
      for (const n of run) n.parentNode?.removeChild(n);
    }
    run = [];
  };
  for (const node of Array.from(root.childNodes)) {
    if (node.nodeType === Node.ELEMENT_NODE && BLOCK.has((node as Element).tagName)) flush(node);
    else run.push(node);
  }
  flush(null);
}

// ---- Editor ---------------------------------------------------------------------------------

interface ToolDef {
  key: string;
  title: string;
  label: string;
  run: () => void;
  state?: () => boolean;
}

/** Turns every `textarea[data-richtext]` on the page into a rich-text editor. */
export function initRichText(): void {
  const areas = document.querySelectorAll<HTMLTextAreaElement>("textarea[data-richtext]");
  if (!areas.length) return;
  // Enter starts a <p> rather than a <div>.
  document.execCommand("defaultParagraphSeparator", false, "p");
  areas.forEach(mount);
}

function mount(ta: HTMLTextAreaElement): void {
  const box = document.createElement("div");
  box.className = "rt";
  const bar = document.createElement("div");
  bar.className = "rt-toolbar";
  bar.setAttribute("role", "toolbar");
  const editor = document.createElement("div");
  editor.className = "rt-editor rich-text";
  editor.contentEditable = "true";
  editor.dir = "rtl";
  editor.setAttribute("role", "textbox");
  editor.setAttribute("aria-multiline", "true");
  const label = ta.closest("div")?.querySelector("label")?.textContent?.trim();
  if (label) editor.setAttribute("aria-label", label);

  const card = ta.parentElement;
  ta.parentNode!.insertBefore(box, ta);
  box.append(bar, editor, ta);
  ta.className = "rt-source"; // the form-input look is the no-script fallback; inside the box it is bare
  ta.dir = "ltr"; // markup reads left to right; the Hebrew inside still runs right to left
  ta.hidden = true;

  let source = false;
  const load = (html: string) => {
    const clean = cleanHtml(html);
    editor.innerHTML = clean;
    ta.value = clean;
  };
  const sync = () => {
    if (!source) ta.value = cleanHtml(editor.innerHTML);
  };
  // After every change: lift nested blocks out (see flatten), then update the posted field.
  const settle = () => {
    flatten(editor);
    sync();
  };
  load(ta.value);

  // ---- caret helpers: commands act on the editor even when it was not the last thing focused ----
  let saved: Range | null = null;
  const inEditor = (n: Node | null) => !!n && editor.contains(n);
  const remember = () => {
    const sel = document.getSelection();
    if (sel && sel.rangeCount && inEditor(sel.anchorNode)) saved = sel.getRangeAt(0).cloneRange();
  };
  const focusEditor = () => {
    const sel = document.getSelection();
    if (sel && sel.rangeCount && inEditor(sel.anchorNode) && document.activeElement === editor) return;
    editor.focus();
    // A remembered caret in content that has since been replaced (back from HTML view) is no use.
    const keep = saved && inEditor(saved.startContainer) ? saved : null;
    const range = keep ?? document.createRange();
    if (!keep) {
      range.selectNodeContents(editor);
      range.collapse(false);
    }
    sel?.removeAllRanges();
    sel?.addRange(range);
  };
  const exec = (cmd: string, value?: string) => {
    focusEditor();
    document.execCommand("styleWithCSS", false, "false");
    document.execCommand(cmd, false, value);
    settle();
    refresh();
  };
  const block = () => (document.queryCommandValue("formatBlock") || "").toLowerCase().replace(/[<>]/g, "");

  const tools: (ToolDef | "|")[] = [
    { key: "h4", title: "כותרת", label: "כותרת", run: () => exec("formatBlock", "<h4>"), state: () => block() === "h4" },
    { key: "h5", title: "כותרת משנה", label: "כותרת משנה", run: () => exec("formatBlock", "<h5>"), state: () => block() === "h5" },
    { key: "p", title: "טקסט רגיל", label: "טקסט", run: () => exec("formatBlock", "<p>"), state: () => block() === "p" },
    "|",
    { key: "bold", title: "מודגש (Ctrl+B)", label: ICONS.bold, run: () => exec("bold"), state: () => document.queryCommandState("bold") },
    { key: "italic", title: "נטוי (Ctrl+I)", label: ICONS.italic, run: () => exec("italic"), state: () => document.queryCommandState("italic") },
    { key: "underline", title: "קו תחתון (Ctrl+U)", label: ICONS.underline, run: () => exec("underline"), state: () => document.queryCommandState("underline") },
    "|",
    { key: "ul", title: "רשימת תבליטים", label: ICONS.ul, run: () => exec("insertUnorderedList"), state: () => document.queryCommandState("insertUnorderedList") },
    { key: "ol", title: "רשימה ממוספרת", label: ICONS.ol, run: () => exec("insertOrderedList"), state: () => document.queryCommandState("insertOrderedList") },
    "|",
    { key: "right", title: "יישור לימין", label: ICONS.alignRight, run: () => exec("justifyRight") },
    { key: "center", title: "מרכוז", label: ICONS.alignCenter, run: () => exec("justifyCenter"), state: () => document.queryCommandState("justifyCenter") },
    { key: "left", title: "יישור לשמאל", label: ICONS.alignLeft, run: () => exec("justifyLeft"), state: () => document.queryCommandState("justifyLeft") },
    "|",
    { key: "link", title: "קישור", label: ICONS.link, run: () => {
      remember();
      const url = window.prompt("כתובת הקישור (https://…)", "https://")?.trim();
      if (!url || url === "https://") return;
      if (!SAFE_HREF.test(url)) {
        window.alert("קישור חייב להתחיל ב-https://, http://, mailto: או tel:");
        return;
      }
      exec("createLink", url);
    } },
    { key: "clear", title: "ניקוי עיצוב", label: ICONS.clear, run: () => { exec("removeFormat"); exec("unlink"); } },
    "|",
    { key: "undo", title: "ביטול (Ctrl+Z)", label: ICONS.undo, run: () => exec("undo") },
    { key: "redo", title: "חזרה (Ctrl+Y)", label: ICONS.redo, run: () => exec("redo") },
  ];

  const buttons: { def: ToolDef; btn: HTMLButtonElement }[] = [];
  const addButton = (def: ToolDef) => {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "rt-btn";
    btn.title = def.title;
    btn.setAttribute("aria-label", def.title);
    btn.innerHTML = def.label;
    // Keep the selection in the editor: a click would otherwise move focus to the button first.
    btn.addEventListener("mousedown", (e) => e.preventDefault());
    btn.addEventListener("click", () => def.run());
    bar.appendChild(btn);
    buttons.push({ def, btn });
    return btn;
  };
  for (const t of tools) {
    if (t === "|") {
      const sep = document.createElement("span");
      sep.className = "rt-sep";
      bar.appendChild(sep);
    } else addButton(t);
  }

  // HTML source view, for whoever wants to paste or fix markup by hand. It goes through the same
  // allow-list on the way back.
  const spacer = document.createElement("span");
  spacer.className = "grow";
  bar.appendChild(spacer);
  const srcBtn = addButton({
    key: "source",
    title: "עריכת קוד HTML",
    label: `${ICONS.code}<span>HTML</span>`,
    run: () => {
      source = !source;
      if (source) {
        ta.value = cleanHtml(editor.innerHTML);
        ta.hidden = false;
        editor.hidden = true;
        ta.focus();
      } else {
        load(ta.value);
        ta.hidden = true;
        editor.hidden = false;
      }
      refresh();
    },
    state: () => source,
  });

  function refresh(): void {
    const sel = document.getSelection();
    const here = !source && !!sel && inEditor(sel.anchorNode);
    for (const { def, btn } of buttons) {
      if (btn !== srcBtn) btn.disabled = source;
      const on = def.key === "source" ? source : here && !!def.state?.();
      btn.classList.toggle("is-on", on);
      btn.setAttribute("aria-pressed", String(on));
    }
  }

  // Emptying the editor (Ctrl+A, Delete) leaves the caret in the shell of the first block, often
  // the heading. Typing there keeps that style, as in any editor, but a paste into an empty editor
  // (or over everything in it) starts from one plain paragraph, so it brings its own structure.
  const resetIfEmpty = (force = false) => {
    if (!force && (editor.textContent?.trim() || editor.querySelector("li, table, hr"))) return;
    editor.innerHTML = "<p><br></p>";
    const range = document.createRange();
    range.setStart(editor.firstChild!, 0);
    range.collapse(true);
    const sel = document.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  };
  const selectsAll = () => {
    const sel = document.getSelection();
    const bare = (s: string) => s.replace(/\s+/g, ""); // the selection's text has line breaks between blocks
    return !!sel && !sel.isCollapsed && bare(sel.toString()) === bare(editor.textContent ?? "");
  };

  editor.addEventListener("input", settle);
  editor.addEventListener("blur", remember);
  document.addEventListener("selectionchange", () => {
    remember();
    refresh();
  });

  // Paste: formatted content goes through the allow-list, plain text stays plain.
  editor.addEventListener("paste", (e) => {
    const cd = e.clipboardData;
    if (!cd) return;
    e.preventDefault();
    resetIfEmpty(selectsAll());
    const html = cd.getData("text/html");
    if (html) document.execCommand("insertHTML", false, cleanHtml(html, ""));
    else document.execCommand("insertText", false, cd.getData("text/plain"));
    settle();
  });

  // Token chips beside the editor insert their token at the caret.
  card?.querySelectorAll<HTMLElement>("[data-rt-token]").forEach((chip) => {
    chip.addEventListener("mousedown", (e) => e.preventDefault());
    chip.addEventListener("click", () => {
      if (source) {
        const at = ta.selectionStart ?? ta.value.length;
        ta.setRangeText(chip.dataset.rtToken!, at, ta.selectionEnd ?? at, "end");
        ta.focus();
      } else exec("insertText", chip.dataset.rtToken!);
    });
  });

  ta.form?.addEventListener("submit", () => {
    ta.value = cleanHtml(source ? ta.value : editor.innerHTML);
  });
  refresh();
}
